import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";

const statusInfo = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("awaiting")) return { label: "بانتظار ردك", cls: "warn" };
  if (s === "open") return { label: "مفتوحة", cls: "info" };
  if (s === "solved") return { label: "محلولة", cls: "ok" };
  if (s === "closed") return { label: "مغلقة", cls: "muted" };
  return { label: status || "—", cls: "muted" };
};

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("ar", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso || "";
  }
};

export default function TicketsCard() {
  const [state, setState] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [sessionText, setSessionText] = useState("");
  const [sessionBusy, setSessionBusy] = useState(false);
  const [sessionMsg, setSessionMsg] = useState(null);
  const [notify5060, setNotify5060] = useState(null); // {sent, sent_at, evidence}
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState(null);
  const pollRef = useRef(null);

  const fetchNotify5060 = useCallback(async () => {
    try {
      const res = await api.get("/monitor/tickets/notify-5060/status");
      setNotify5060(res.data);
    } catch (e) { /* silent */ }
  }, []);

  const sendNotify5060 = async () => {
    setNotifyBusy(true);
    setNotifyMsg(null);
    try {
      const res = await api.post("/monitor/tickets/notify-5060", {}, { timeout: 300000 });
      if (res.data.sent) {
        setNotifyMsg({ ok: true, text: `تم إرسال الرد لـ Ookla بنجاح — المنفذ مفتوح من ${res.data.port?.open ?? "?"} دول (${res.data.evidence || ""})` });
      } else if (res.data.already) {
        setNotifyMsg({ ok: true, text: "سبق أن أُرسل الرد لـ Ookla" });
      }
      fetchNotify5060();
    } catch (e) {
      setNotifyMsg({ ok: false, text: e?.response?.data?.detail || "فشل الإرسال — أعد المحاولة" });
    }
    setNotifyBusy(false);
  };

  const fetchTickets = useCallback(async () => {
    try {
      const res = await api.get("/monitor/tickets", { timeout: 20000 });
      setState(res.data);
      return res.data;
    } catch (e) {
      if (e?.response?.status !== 401) console.error("tickets fetch error", e);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchNotify5060();
    const t = setInterval(fetchTickets, 90000);
    return () => {
      clearInterval(t);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchTickets, fetchNotify5060]);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      await api.post("/monitor/tickets/check", {}, { timeout: 15000 });
    } catch (e) {
      console.error("tickets check error", e);
    }
    // poll until the background run finishes (max ~4 min)
    let tries = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      tries += 1;
      const data = await fetchTickets();
      if ((data && !data.running) || tries > 24) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRefreshing(false);
      }
    }, 10000);
  };

  const tickets = state?.tickets || [];
  const busy = refreshing || state?.running;
  const sessionExpired = (state?.last_error || "").includes("session_expired");

  const startPolling = () => {
    let tries = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      tries += 1;
      const data = await fetchTickets();
      if ((data && !data.running) || tries > 24) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRefreshing(false);
        setSessionBusy(false);
      }
    }, 10000);
  };

  const onSessionFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSessionText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const uploadSession = async () => {
    if (!sessionText.trim()) {
      setSessionMsg({ ok: false, text: "الصق محتوى ملف ookla_state.json أو اختر الملف أولاً" });
      return;
    }
    setSessionBusy(true);
    setSessionMsg(null);
    try {
      await api.post("/monitor/tickets/session", { state_json: sessionText }, { timeout: 20000 });
      setSessionMsg({ ok: true, text: "تم رفع الجلسة — جاري إعادة تفعيل المراقبة الآن..." });
      setSessionText("");
      setRefreshing(true);
      startPolling();
    } catch (e) {
      setSessionBusy(false);
      setSessionMsg({ ok: false, text: e?.response?.data?.detail || "فشل رفع الجلسة — تأكد من صحة الملف" });
    }
  };

  return (
    <section className="card tickets-card" data-testid="tickets-card">
      <div className="card-head">
        <h2>🎫 تذاكر دعم Ookla</h2>
        <div className="monitor-head-actions">
          {state?.last_checked && (
            <span className="last-check" data-testid="tickets-last-checked">
              آخر تحديث: {fmtDateTime(state.last_checked)}
            </span>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={refreshNow}
            disabled={busy}
            data-testid="tickets-refresh-btn"
          >
            {busy ? <span className="spinner" /> : "🔄"} {busy ? "جاري الفحص لدى Ookla..." : "تحديث الآن"}
          </button>
        </div>
      </div>

      <p className="tickets-sub">
        مراقبة تلقائية كل 30 دقيقة — تنبيه تيليجرام فوري عند أي رد أو تغيير حالة
      </p>

      {/* ===== One-click: tell Ookla port 5060 is now open ===== */}
      <div className="notify5060-panel" data-testid="notify-5060-panel">
        <div className="notify5060-info">
          <b>📨 إبلاغ Ookla بفتح المنفذ 5060</b>
          {notify5060?.sent ? (
            <span className="notify5060-done" data-testid="notify-5060-done">
              ✅ أُرسل الرد في {fmtDateTime(notify5060.sent_at)} — الدليل: {notify5060.evidence}
            </span>
          ) : (
            <span className="notify5060-hint">
              اضغط فور فتح مزوّد الإنترنت للمنفذ — سيتم التحقق دولياً أولاً (من 6 دول) ثم يُرسل الرد على التذكرة #1308526 تلقائياً
            </span>
          )}
        </div>
        {!notify5060?.sent && (
          <button
            className="btn btn-primary btn-sm"
            onClick={sendNotify5060}
            disabled={notifyBusy}
            data-testid="notify-5060-send-button"
          >
            {notifyBusy ? <span className="spinner" /> : "🚀"}{" "}
            {notifyBusy ? "يفحص المنفذ دولياً ثم يرسل..." : "تحقق وأرسل الرد الآن"}
          </button>
        )}
        {notifyMsg && (
          <div className={`session-msg ${notifyMsg.ok ? "ok" : "fail"}`} data-testid="notify-5060-msg">
            {notifyMsg.ok ? "✅" : "⚠️"} {notifyMsg.text}
          </div>
        )}
      </div>

      {sessionExpired ? (
        <div className="session-expired-panel" data-testid="session-expired-panel">
          <div className="session-expired-head">
            🔒 <b>انتهت جلسة الدخول لمنصة Ookla — المراقبة متوقفة مؤقتاً</b>
          </div>
          <p className="session-expired-note">
            Ookla تحجب تسجيل الدخول من عنوان IP هذا السيرفر، لذا يلزم توليد الجلسة من جهاز خارجي:
            سجّل الدخول إلى support.ookla.com من متصفح خارجي، صدّر ملف الجلسة (ookla_state.json)،
            ثم ارفعه هنا لإعادة تفعيل المراقبة فوراً.
          </p>
          <div className="session-actions">
            <label className="btn btn-ghost btn-sm session-file-label">
              📂 اختيار ملف الجلسة
              <input
                type="file"
                accept=".json,application/json,.txt"
                onChange={onSessionFile}
                style={{ display: "none" }}
                data-testid="session-file-input"
              />
            </label>
            <button
              className="btn btn-primary btn-sm"
              onClick={uploadSession}
              disabled={sessionBusy}
              data-testid="session-upload-btn"
            >
              {sessionBusy ? <span className="spinner" /> : "🔄"} {sessionBusy ? "جاري إعادة التفعيل..." : "رفع الجلسة وإعادة التفعيل"}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={refreshNow}
              disabled={busy}
              data-testid="session-retry-btn"
            >
              إعادة المحاولة بالجلسة الحالية
            </button>
          </div>
          <textarea
            className="session-textarea"
            dir="ltr"
            rows={3}
            placeholder='أو الصق محتوى ookla_state.json هنا... {"cookies": [...]}'
            value={sessionText}
            onChange={(e) => setSessionText(e.target.value)}
            data-testid="session-textarea"
          />
          {sessionMsg && (
            <div className={`session-msg ${sessionMsg.ok ? "ok" : "fail"}`} data-testid="session-msg">
              {sessionMsg.ok ? "\u2705" : "\u26A0\uFE0F"} {sessionMsg.text}
            </div>
          )}
        </div>
      ) : (
        state?.last_run_ok === false && (
          <div className="monitor-error" data-testid="tickets-error">
            ⚠️ تعذر جلب التذاكر في آخر محاولة — ستتم إعادة المحاولة تلقائياً
          </div>
        )
      )}

      {!state && (
        <div className="monitor-loading" data-testid="tickets-loading">
          <span className="spinner big" /> جاري تحميل التذاكر...
        </div>
      )}

      {state && tickets.length === 0 && (
        <div className="tickets-empty" data-testid="tickets-empty">
          لا توجد تذاكر بعد — اضغط "تحديث الآن" لجلبها من Ookla
        </div>
      )}

      {tickets.length > 0 && (
        <div className="tickets-list" data-testid="tickets-list">
          {tickets.map((t) => {
            const st = statusInfo(t.status);
            const comment = t.latest_comment;
            const isOpen = !!expanded[t.id];
            return (
              <div key={t.id} className={`ticket-item ${st.cls}`} data-testid={`ticket-item-${t.id}`}>
                <div className="ticket-head">
                  <div className="ticket-title">
                    <span className="ticket-id" dir="ltr">#{t.id}</span>
                    <span className="ticket-subject">{t.subject}</span>
                  </div>
                  <span className={`ticket-badge ${st.cls}`} data-testid={`ticket-status-${t.id}`}>
                    {st.label}
                  </span>
                </div>
                <div className="ticket-meta">
                  <span>🕐 آخر نشاط: {t.last_activity}</span>
                  <a
                    className="ticket-link"
                    href={`https://support.ookla.com/hc/en-us/requests/${t.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`ticket-link-${t.id}`}
                  >
                    فتح في Ookla ↗
                  </a>
                </div>
                {st.cls === "warn" && (
                  <div className="ticket-warn-note" data-testid={`ticket-warn-${t.id}`}>
                    ⚠️ Ookla تنتظر ردك — التذكرة تُغلق تلقائياً إن لم يصل رد خلال أيام
                  </div>
                )}
                {comment?.text && (
                  <div className="ticket-comment">
                    <button
                      className="ticket-comment-toggle"
                      onClick={() => setExpanded((e) => ({ ...e, [t.id]: !isOpen }))}
                      data-testid={`ticket-comment-toggle-${t.id}`}
                    >
                      💬 آخر رد ({fmtDateTime(comment.created_at)}) {isOpen ? "▲" : "▼"}
                    </button>
                    {isOpen && (
                      <pre className="ticket-comment-body" dir="ltr" data-testid={`ticket-comment-body-${t.id}`}>
                        {comment.text}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {state?.server_status && (
        <div className="server-status-strip" data-testid="server-status-strip">
          <span className="sss-item">
            📡 شبكة Speedtest: <b>{state.server_status.network || "—"}</b>
          </span>
          <span className="sss-item">
            ❤️ صحة السيرفر: <b>{state.server_status.health || "—"}</b>
          </span>
          <span className="sss-item">
            📍 الموقع المسجّل: <b>{state.server_status.location || "—"}</b>
          </span>
        </div>
      )}
    </section>
  );
}
